import { useState, useCallback, useRef } from 'react';
import { useToast } from '@practium/ui';
import { useAuth } from '@practium/features/auth';
import type { PlantAnalysisFormData } from '../types/analyses.types';
import { PlantAnalysisSchema } from '../schemas/analyses.schema';
import { DEFAULT_PLANT_FORM } from '../constants/analyses.constants';
import { mapPlantFormToCreatePayload } from '../utils/analysisApiMappers';
import { usePostPlantAnalysis, useUpdatePlantAnalysis } from '@practium/features/api';

export interface UsePlantAnalysisFormOptions {
	initialData?: PlantAnalysisFormData;
	onSubmit?: () => void;
	analysisId?: string;
	onLoadingChange?: (loading: boolean) => void;
}

export const usePlantAnalysisForm = ({
	initialData,
	onSubmit,
	analysisId,
	onLoadingChange,
}: UsePlantAnalysisFormOptions) => {
	const toast = useToast();
	const { user } = useAuth();
	const initialForm = initialData ? { ...initialData } : { ...DEFAULT_PLANT_FORM };
	const [form, setForm] = useState<PlantAnalysisFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState(false);
	const { mutateAsync: postPlantAnalysis } = usePostPlantAnalysis();
	const { mutateAsync: updatePlantAnalysis } = useUpdatePlantAnalysis();

	const set = useCallback(
		<K extends keyof PlantAnalysisFormData>(field: K, value: PlantAnalysisFormData[K]) => {
			setForm((prev) => ({ ...prev, [field]: value }));
			setErrors((prev) => {
				if (!prev[field]) return prev;
				const next = { ...prev };
				delete next[field as string];
				return next;
			});
		},
		[]
	);

	const populate = useCallback((data: PlantAnalysisFormData) => {
		setForm({ ...data });
		setErrors({});
	}, []);

	const isDirty = useCallback(
		() => JSON.stringify(form) !== JSON.stringify(initialFormRef.current),
		[form]
	);

	const reset = useCallback(() => {
		setForm(initialData ? { ...initialData } : { ...DEFAULT_PLANT_FORM });
		setErrors({});
	}, [initialData]);

	const handleSubmit = useCallback(
		async (options?: { showSuccessToast?: boolean }) => {
			const showSuccessToast = options?.showSuccessToast ?? true;
			const result = PlantAnalysisSchema.safeParse(form);
			if (!result.success) {
				const fieldErrors: Record<string, string> = {};
				for (const issue of result.error.issues) {
					const key = issue.path[0] as string;
					if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
				}
				setErrors(fieldErrors);
				toast.error('Corrija os campos destacados antes de salvar.');
				return false;
			}
			if (!user?._id) {
				toast.error('Usuário autenticado não encontrado.');
				return false;
			}

			setIsLoading(true);
			onLoadingChange?.(true);
			try {
				const payload = mapPlantFormToCreatePayload(form, user._id);
				if (analysisId) {
					await updatePlantAnalysis({ id: analysisId, data: payload });
				} else {
					await postPlantAnalysis(payload);
				}
				if (showSuccessToast) {
					toast.success(
						analysisId
							? 'Análise foliar atualizada com sucesso!'
							: 'Análise foliar salva com sucesso!'
					);
				}
				if (!analysisId) reset();
				onSubmit?.();
				return true;
			} catch (err) {
				const msg = err instanceof Error ? err.message : 'Erro ao salvar análise foliar';
				toast.error(msg);
				return false;
			} finally {
				setIsLoading(false);
				onLoadingChange?.(false);
			}
		},
		[
			form,
			user?._id,
			toast,
			analysisId,
			updatePlantAnalysis,
			postPlantAnalysis,
			reset,
			onSubmit,
			onLoadingChange,
		]
	);

	return { form, errors, isLoading, set, populate, reset, handleSubmit, isDirty };
};
